import React, { useEffect, useState } from 'react';
import { Button, Layout, Table, Timeline, type TableProps, Spin, Empty, Image } from 'antd';
import { useParams } from 'react-router-dom';
import Navbar from '../../components/Layout/Navbar';
import './DetailPage.css';
import { ContentDetailTableKey, type ContentDetailData, sampleContentDetailData, type UseCaseDetails } from '../Admin/utils/contentDetailTypes';
import { type DXBlueprintContent } from '../../services/contentService';
import { useLanguage } from '../../contexts/LanguageContext';
import { fileService } from '../../services/fileService';

import dx1Bg from '../../assets/backgrounds/dx1.mp4';
import dx2Bg from '../../assets/backgrounds/dx2.mp4';
import dx3Bg from '../../assets/backgrounds/dx3.mp4';
import dx4Bg from '../../assets/backgrounds/dx4.mp4';
import dx5Bg from '../../assets/backgrounds/dx5.mp4';
import dx6Bg from '../../assets/backgrounds/dx6.mp4';
import dx7Bg from '../../assets/backgrounds/dx7.mp4';
import dx8Bg from '../../assets/backgrounds/dx8.mp4';
import dx9Bg from '../../assets/backgrounds/dx9.mp4';
import dx10Bg from '../../assets/backgrounds/dx10.mp4';
import dx11Bg from '../../assets/backgrounds/dx11.mp4';
import dx12Bg from '../../assets/backgrounds/dx12.mp4';
import dx13Bg from '../../assets/backgrounds/dx13.mp4';
import dx14Bg from '../../assets/backgrounds/dx14.mp4';
import dxLogo from '../../assets/LOGO_DX_PNG.png';
import { ArrowDownOutlined, ArrowUpOutlined, CalendarOutlined, LineChartOutlined, SettingOutlined } from '@ant-design/icons';
import { ConfigProvider } from 'antd';

interface DataType {
  currentStep: string;
  nextMove: string;
  potential: string;
}
const columns: TableProps<DataType>['columns'] = [
  {
    title: 'Current Step Stones',
    dataIndex: 'currentStep',
    key: 'currentStep',
  },
  {
    title: 'Next Move',
    dataIndex: 'nextMove',
    key: 'nextMove',
  },
  {
    title: 'Potential Value Creations',
    dataIndex: 'potential',
    key: 'potential',
  }
];

const { Content } = Layout;

interface DetailProps {
  data?: ContentDetailData;
  overrideId?: string;
  languageProp?: 'TH' | 'EN' | undefined;
  onBack?: () => void;
}

const DetailPage: React.FC<DetailProps> = ({ data, overrideId, languageProp, onBack }) => {
  const { id } = useParams<{ id: string }>();
  const { language, setLanguage } = useLanguage();

  // Force set language on component mount if languageProp is provided
  useEffect(() => {
    if (languageProp) {
      setLanguage(languageProp);
    }
  }, [languageProp, setLanguage]);

  // State for API data fetching
  const [apiData, setApiData] = useState<ContentDetailData | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Transform API data to ContentDetailData format
  const transformApiToDetailData = (apiContent: DXBlueprintContent): ContentDetailData => {
    return {
      [ContentDetailTableKey.Overview]: {
        dxNo: apiContent.dx_number || '',
        titleTh: apiContent.title_th || '',
        titleEn: apiContent.title_en || '',
        shortDescTh: apiContent.short_description_th || '',
        shortDescEn: apiContent.short_description_en || '',
      },
      [ContentDetailTableKey.ProblemStatement]: {
        statementTh: apiContent.problem_statement_th || '',
        statementEn: apiContent.problem_statement_en || '',
      },
      [ContentDetailTableKey.PainPoint]: apiContent.pain_point_items?.map(item => ({
        titleTh: item.title_th || '',
        titleEn: item.title_en || '',
        descTh: item.description_th || '',
        descEn: item.description_en || '',
      })) || [{ titleTh: '-', titleEn: '-', descTh: '-', descEn: '-' }],
      [ContentDetailTableKey.GapToImprove]: apiContent.gap_to_improve_items?.map(item => ({
        titleTh: item.title_th || '',
        titleEn: item.title_en || '',
        descTh: item.description_th || '',
        descEn: item.description_en || '',
      })) || [{ titleTh: '-', titleEn: '-', descTh: '-', descEn: '-' }],
      [ContentDetailTableKey.StrategicObjective]: apiContent.strategic_objectives_th || apiContent.strategic_objectives_en ? 
        apiContent.strategic_objectives_th?.split('\n').map((obj, index) => ({
          objectiveTh: obj.trim() || '',
          objectiveEn: apiContent.strategic_objectives_en?.split('\n')[index]?.trim() || '',
        })).filter(item => item.objectiveTh || item.objectiveEn) || 
        [{ objectiveTh: apiContent.strategic_objectives_th || '', objectiveEn: apiContent.strategic_objectives_en || '' }]
        : [{ objectiveTh: '-', objectiveEn: '-' }],
      [ContentDetailTableKey.BusinessImpact]: apiContent.business_impacts_th || apiContent.business_impacts_en ? 
        apiContent.business_impacts_th?.split('\n').map((impact, index) => ({
          impactTh: impact.trim() || '',
          impactEn: apiContent.business_impacts_en?.split('\n')[index]?.trim() || '',
        })).filter(item => item.impactTh || item.impactEn) || 
        [{ impactTh: apiContent.business_impacts_th || '', impactEn: apiContent.business_impacts_en || '' }]
        : [{ impactTh: '-', impactEn: '-' }],
      [ContentDetailTableKey.EndGoals]: apiContent.end_goals_th || apiContent.end_goals_en ? 
        apiContent.end_goals_th?.split('\n').map((goal, index) => ({
          goalTh: goal.trim() || '',
          goalEn: apiContent.end_goals_en?.split('\n')[index]?.trim() || '',
        })).filter(item => item.goalTh || item.goalEn) || 
        [{ goalTh: apiContent.end_goals_th || '', goalEn: apiContent.end_goals_en || '' }]
        : [{ goalTh: '-', goalEn: '-' }],
      [ContentDetailTableKey.Pillar]: apiContent.key_capabilities_pillars_th || apiContent.key_capabilities_pillars_en ? 
        apiContent.key_capabilities_pillars_th?.split('\n').map((pillar, index) => ({
          pillarTh: pillar.trim() || '',
          pillarEn: apiContent.key_capabilities_pillars_en?.split('\n')[index]?.trim() || '',
        })).filter(item => item.pillarTh || item.pillarEn) || 
        [{ pillarTh: apiContent.key_capabilities_pillars_th || '', pillarEn: apiContent.key_capabilities_pillars_en || '' }]
        : [{ pillarTh: '-', pillarEn: '-' }],
      [ContentDetailTableKey.UseCases]: apiContent.use_case_items?.map(item => ({
        nameTh: item.name_th || '',
        nameEn: item.name_en || '',
        details: item.use_case_details?.map(detail => ({
          currentStepTh: detail.current_step_stones_th || '',
          currentStepEn: detail.current_step_stones_en || '',
          nextMoveTh: detail.next_move_th || '',
          nextMoveEn: detail.next_move_en || '',
          potentialValueTh: detail.potential_value_creations_th || '',
          potentialValueEn: detail.potential_value_creations_en || '',
        })) || [{
          currentStepTh: item.description_th || '',
          currentStepEn: item.description_en || '',
          nextMoveTh: '-',
          nextMoveEn: '-',
          potentialValueTh: '-',
          potentialValueEn: '-',
        }]
      })) || [{
        nameTh: '-',
        nameEn: '-',
        details: [{
          currentStepTh: '-',
          currentStepEn: '-',
          nextMoveTh: '-',
          nextMoveEn: '-',
          potentialValueTh: '-',
          potentialValueEn: '-',
        }]
      }],
      [ContentDetailTableKey.Technology]: {
        titleTh: apiContent.technologies_enablers_items?.[0]?.title_th || 
                (apiContent.technologies_enablers_items?.length ? 
                  apiContent.technologies_enablers_items.map(tech => tech.title_th).filter(Boolean).join(', ') : '') || '',
        titleEn: apiContent.technologies_enablers_items?.[0]?.title_en || 
                (apiContent.technologies_enablers_items?.length ? 
                  apiContent.technologies_enablers_items.map(tech => tech.title_en).filter(Boolean).join(', ') : '') || '',
      },
      [ContentDetailTableKey.KeyChallenges]: apiContent.key_challenges_items?.map(item => ({
        challengeTh: item.title_th || '',
        challengeEn: item.title_en || '',
      })) || [{ challengeTh: '-', challengeEn: '-' }],
      [ContentDetailTableKey.TechnologyImage]: {
        images: apiContent.technologies_enablers_images || []
      },
      [ContentDetailTableKey.Timeline]: apiContent.timeline_items?.map(item => ({
        phaseNameTh: item.phase_th || '',
        phaseNameEn: item.phase_en || '',
        durationTh: item.timeline_th || '',
        durationEn: item.timeline_en || '',
        status: 'published' as const
      })) || [{
        phaseNameTh: '-',
        phaseNameEn: '-',
        durationTh: '-',
        durationEn: '-',
        status: 'inProgress' as const
      }],
      [ContentDetailTableKey.Permission]: {
        owner: '',
        member: []
      }
    };
  };

  // Fetch content data from API when component mounts and no prop data is provided
  useEffect(() => {
    const fetchContentData = async () => {
      // Only fetch if no prop data is provided and id exists
      if (!data && id) {
        try {
          setIsLoading(true);
          const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'https://api.your-backend.com'}/contents/${id}`);
          const data: DXBlueprintContent = await response.json();
          if (data) {
            const transformedData = transformApiToDetailData(data);
            setApiData(transformedData);
          }
        } catch (error) {
          console.error('❌ Failed to fetch content detail:', error);
        } finally {
          setIsLoading(false);
        }
      }
    };

    fetchContentData();
  }, [id, data]);

  // Determine which data to use: prop data > API data > sample data
  const detail: ContentDetailData = data || apiData || sampleContentDetailData;

  // Component to handle image loading from asset SKs
  const TechnologyImageDisplay: React.FC<{ assetSK: string }> = ({ assetSK }) => {
    const [imageUrl, setImageUrl] = useState<string>('');
    const [loading, setLoading] = useState(true);

    useEffect(() => {
      const loadImage = async () => {
        try {
          const response = await fileService.getPublicViewUrls([assetSK]);
          if (response.urls.length > 0) {
            setImageUrl(response.urls[0].presigned_url);
          }
        } catch (error) {
          console.error('Failed to load image:', error);
        } finally {
          setLoading(false);
        }
      };

      loadImage();
    }, [assetSK]);

    if (loading) {
      return (
        <div className="technology-image-loading">
          <Spin size="small" />
        </div>
      );
    }

    if (!imageUrl) {
      return null;
    }

    return (
      <div className="technology-image-wrapper">
        <Image 
          src={imageUrl || "../../src/assets/placeholder.png"} 
          alt={`Asset ${assetSK}`}
          preview={{
            mask: false,
          }}
        />
      </div>
    );
  };

  // Helper for background video
  const getBackgroundByID = (id: string | undefined): string => {
    let dx = 'dx1';
    if (id) dx = id;
    if (overrideId) dx = overrideId;
    const iconKey = dx.replace('-', '').toLowerCase();
    const iconMap: { [key: string]: string } = {
      'dx1': dx1Bg,
      'dx2': dx2Bg,
      'dx3': dx3Bg,
      'dx4': dx4Bg,
      'dx5': dx5Bg,
      'dx6': dx6Bg,
      'dx7': dx7Bg,
      'dx8': dx8Bg,
      'dx9': dx9Bg,
      'dx10': dx10Bg,
      'dx11': dx11Bg,
      'dx12': dx12Bg,
      'dx13': dx13Bg,
      'dx14': dx14Bg,
    };
    return iconMap[iconKey] || dx1Bg;
  };
  const backgroundVideo = getBackgroundByID(id);

  // Helper functions to check if data is empty or has meaningful content
  const isEmptyOrDash = (value: string | undefined | null): boolean => {
    return !value || value.trim() === '' || value.trim() === '-';
  };

  const hasValidData = (items: any[], fieldCheck: (item: any) => boolean): boolean => {
    return items.length > 0 && items.some(fieldCheck);
  };

  // Check if sections have valid data
  const hasProblemStatement = !isEmptyOrDash(detail[ContentDetailTableKey.ProblemStatement].statementTh) ||
    !isEmptyOrDash(detail[ContentDetailTableKey.ProblemStatement].statementEn);

  const hasPainPoints = hasValidData(detail[ContentDetailTableKey.PainPoint], item =>
    !isEmptyOrDash(item.titleTh) || !isEmptyOrDash(item.titleEn) ||
    !isEmptyOrDash(item.descTh) || !isEmptyOrDash(item.descEn)
  );

  const hasGapToImprove = hasValidData(detail[ContentDetailTableKey.GapToImprove], item =>
    !isEmptyOrDash(item.titleTh) || !isEmptyOrDash(item.titleEn) ||
    !isEmptyOrDash(item.descTh) || !isEmptyOrDash(item.descEn)
  );

  const hasStrategicObjectives = hasValidData(detail[ContentDetailTableKey.StrategicObjective], item =>
    !isEmptyOrDash(item.objectiveTh) || !isEmptyOrDash(item.objectiveEn)
  );

  const hasBusinessImpacts = hasValidData(detail[ContentDetailTableKey.BusinessImpact], item =>
    !isEmptyOrDash(item.impactTh) || !isEmptyOrDash(item.impactEn)
  );

  const hasEndGoals = hasValidData(detail[ContentDetailTableKey.EndGoals], item =>
    !isEmptyOrDash(item.goalTh) || !isEmptyOrDash(item.goalEn)
  );

  const hasPillars = hasValidData(detail[ContentDetailTableKey.Pillar], item =>
    !isEmptyOrDash(item.pillarTh) || !isEmptyOrDash(item.pillarEn)
  );

  const hasUseCases = hasValidData(detail[ContentDetailTableKey.UseCases], item => {
    const hasName = !isEmptyOrDash(item.nameTh) || !isEmptyOrDash(item.nameEn);
    const hasDetails = Array.isArray(item.details) ?
      item.details.some((detail: any) =>
        !isEmptyOrDash(detail.currentStepTh) || !isEmptyOrDash(detail.currentStepEn) ||
        !isEmptyOrDash(detail.nextMoveTh) || !isEmptyOrDash(detail.nextMoveEn) ||
        !isEmptyOrDash(detail.potentialValueTh) || !isEmptyOrDash(detail.potentialValueEn)
      ) : false;
    return hasName || hasDetails;
  });

  const hasTechnology = !isEmptyOrDash(detail[ContentDetailTableKey.Technology].titleTh) ||
    !isEmptyOrDash(detail[ContentDetailTableKey.Technology].titleEn);

  const hasKeyChallenges = hasValidData(detail[ContentDetailTableKey.KeyChallenges], item =>
    !isEmptyOrDash(item.challengeTh) || !isEmptyOrDash(item.challengeEn)
  );

  const hasTechnologyImages = detail[ContentDetailTableKey.TechnologyImage].images.length > 0 &&
    detail[ContentDetailTableKey.TechnologyImage].images.some(img => !isEmptyOrDash(img));

  const hasTimeline = hasValidData(detail[ContentDetailTableKey.Timeline], item =>
    !isEmptyOrDash(item.phaseNameTh) || !isEmptyOrDash(item.phaseNameEn) ||
    !isEmptyOrDash(item.durationTh) || !isEmptyOrDash(item.durationEn)
  );

  // Check if entire sections should be shown
  const showBeforeTransformation = hasProblemStatement || hasPainPoints || hasGapToImprove;
  const showTransformationBlueprint = hasStrategicObjectives || hasBusinessImpacts || hasEndGoals || hasPillars;
  const showTechnologiesAndEnablers = hasTechnology || hasKeyChallenges || hasTechnologyImages;

  // Renderers for each section
  const informationCard = (title: string, description: string) => (
    <div className="information-card">
      <h3 className="information-card-title">{title}</h3>
      <p className="information-card-description">{description}</p>
    </div>
  );

  // Use Cases Table
  const renderUseCaseTable = (useCase: ContentDetailData[typeof ContentDetailTableKey.UseCases][number], idx: number) => {
    const { nameTh, nameEn, details } = useCase;
    const data: DataType[] = Array.isArray(details)
      ? details
        .filter((detail: UseCaseDetails) =>
          !isEmptyOrDash(detail.currentStepTh) || !isEmptyOrDash(detail.currentStepEn) ||
          !isEmptyOrDash(detail.nextMoveTh) || !isEmptyOrDash(detail.nextMoveEn) ||
          !isEmptyOrDash(detail.potentialValueTh) || !isEmptyOrDash(detail.potentialValueEn)
        )
        .map((detail: UseCaseDetails) => ({
          currentStep: language === 'TH' ? detail.currentStepTh : detail.currentStepEn,
          nextMove: language === 'TH' ? detail.nextMoveTh : detail.nextMoveEn,
          potential: language === 'TH' ? detail.potentialValueTh : detail.potentialValueEn,
        }))
      : [];

    // Only render if there's valid data
    if (data.length === 0 && (isEmptyOrDash(nameTh) && isEmptyOrDash(nameEn))) {
      return null;
    }

    return (
      <div>
        <div className='table-header'>
          <div className='table-topic'>USECASE {idx + 1}</div>
          <h2>{language === 'TH' ? nameTh : nameEn}</h2>
        </div>
        {data.length > 0 && <Table<DataType> columns={columns} dataSource={data} pagination={false} className="gradient-header-table" />}
      </div>
    );
  };

  // Timeline items
  const renderTimelineItems = () =>
    detail[ContentDetailTableKey.Timeline]
      .filter(item => !isEmptyOrDash(item.phaseNameTh) || !isEmptyOrDash(item.phaseNameEn) ||
        !isEmptyOrDash(item.durationTh) || !isEmptyOrDash(item.durationEn))
      .map((item, idx) => ({
        color: 'black',
        dot:
          idx === 0 ? <CalendarOutlined /> : idx === 1 ? <SettingOutlined /> : <LineChartOutlined />,
        children: (
          <>
            <p style={{ fontSize: '20px' }}>{language === 'TH' ? item.phaseNameTh : item.phaseNameEn}</p>
            <p style={{ fontSize: '16px' }}>{language === 'TH' ? item.durationTh : item.durationEn}</p>
          </>
        ),
      }));

  // Helper for scroll
  const handleScrollDown = () => {
    const contentSection = document.querySelector('.information-section');
    if (contentSection) {
      contentSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Show loading state when fetching API data
  if (!data && isLoading) {
    return (
      <Layout className="detail-layout">
        <Navbar onBack={onBack} />
        <Content className="detail-content" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
          <Spin size="large" />
          <div style={{ marginLeft: '16px', fontSize: '16px' }}>
            {language === 'TH' ? 'กำลังโหลดข้อมูล...' : 'Loading data...'}
          </div>
        </Content>
      </Layout>
    );
  }

  return (
    <Layout className="detail-layout">
      <Navbar onBack={onBack} />
      <Content className="detail-content">
        {/* Video Background Section */}
        <div className="video-background-section">
          <video autoPlay muted loop className="background-video">
            <source src={backgroundVideo} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
          {(data || apiData) && (
            <div className='dx-card'>
              <div>
                <p>DIGITAL TRANSFORMATION BLUEPRINT</p>
                <h1>{detail[ContentDetailTableKey.Overview].dxNo}</h1>
                <h2>{language === 'TH' ? detail[ContentDetailTableKey.Overview].titleTh : detail[ContentDetailTableKey.Overview].titleEn}</h2>
                <p className='dx-description'>{language === 'TH' ? detail[ContentDetailTableKey.Overview].shortDescTh : detail[ContentDetailTableKey.Overview].shortDescEn}</p>
              </div>
              <div style={{
                position: 'absolute',
                bottom: '20px',
                left: '50%',
                transform: 'translateX(-50%)',
                width: '100%',
                display: 'flex',
                justifyContent: 'center'
              }}>
                <Button
                  type="primary"
                  size="large"
                  className="start-button"
                  onClick={handleScrollDown}
                  icon={
                    <div style={{
                      backgroundColor: 'rgba(0, 0, 0, 0)',
                      borderColor: 'rgba(217, 217, 217, 1)',
                      borderWidth: '0.57px',
                      borderStyle: 'solid',
                      borderRadius: '50%',
                      width: '24px',
                      height: '24px',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center'
                    }}>
                      <ArrowDownOutlined />
                    </div>
                  }
                  iconPosition='end'
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}
                >
                  Explore
                </Button>
              </div>
            </div>
          )}
        </div>
        <div className="information-section">
          {data || apiData ? (
            <div>
              {showBeforeTransformation && (
                <div className="information">
                  <h2 className="information-title">Before Transformation</h2>
                  <div className='information-content'>
                    {hasProblemStatement && (
                      <div className='information-item'>
                        <div className='topic'>PROBLEM STATEMENT</div>
                        <p>{language === 'TH' ? detail[ContentDetailTableKey.ProblemStatement].statementTh : detail[ContentDetailTableKey.ProblemStatement].statementEn}</p>
                      </div>
                    )}
                    {hasPainPoints && (
                      <div className='information-item'>
                        <div className='topic'>PAINPOINT</div>
                        <div className='information-card-container'>
                          {detail[ContentDetailTableKey.PainPoint]
                            .filter(item => !isEmptyOrDash(item.titleTh) || !isEmptyOrDash(item.titleEn) ||
                              !isEmptyOrDash(item.descTh) || !isEmptyOrDash(item.descEn))
                            .map((item, idx) => (
                              <div key={idx}>
                                {informationCard(
                                  language === 'TH' ? item.titleTh : item.titleEn,
                                  language === 'TH' ? item.descTh : item.descEn
                                )}
                              </div>
                            ))}
                        </div>
                      </div>
                    )}
                    {hasGapToImprove && (
                      <div className='information-item'>
                        <div className='topic'>GAP TO IMPROVE</div>
                        <div className='information-card-container'>
                          {detail[ContentDetailTableKey.GapToImprove]
                            .filter(item => !isEmptyOrDash(item.titleTh) || !isEmptyOrDash(item.titleEn) ||
                              !isEmptyOrDash(item.descTh) || !isEmptyOrDash(item.descEn))
                            .map((item, idx) => (
                              <div key={idx}>
                                {informationCard(
                                  language === 'TH' ? item.titleTh : item.titleEn,
                                  language === 'TH' ? item.descTh : item.descEn
                                )}
                              </div>
                            ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
              {showTransformationBlueprint && (
                <div className="information">
                  <h2 className="information-title">Transformation Blueprint</h2>
                  <div className='information-content'>
                    {hasStrategicObjectives && (
                      <div className='information-item'>
                        <div className='topic'>STRATEGIC OBJECTIVES</div>
                        <ul className="capabilities-list">
                          {detail[ContentDetailTableKey.StrategicObjective]
                            .filter(item => !isEmptyOrDash(item.objectiveTh) || !isEmptyOrDash(item.objectiveEn))
                            .map((item, idx) => (
                              <li key={idx}>{language === 'TH' ? item.objectiveTh : item.objectiveEn}</li>
                            ))}
                        </ul>
                      </div>
                    )}
                    {hasBusinessImpacts && (
                      <div className='information-item'>
                        <div className='topic'>BUSINESS IMPACTS</div>
                        <ul className="capabilities-list">
                          {detail[ContentDetailTableKey.BusinessImpact]
                            .filter(item => !isEmptyOrDash(item.impactTh) || !isEmptyOrDash(item.impactEn))
                            .map((item, idx) => (
                              <li key={idx}>{language === 'TH' ? item.impactTh : item.impactEn}</li>
                            ))}
                        </ul>
                      </div>
                    )}
                    {hasEndGoals && (
                      <div className='information-item'>
                        <div className='topic'>END GOALS</div>
                        <ul className="capabilities-list">
                          {detail[ContentDetailTableKey.EndGoals]
                            .filter(item => !isEmptyOrDash(item.goalTh) || !isEmptyOrDash(item.goalEn))
                            .map((item, idx) => (
                              <li key={idx}>{language === 'TH' ? item.goalTh : item.goalEn}</li>
                            ))}
                        </ul>
                      </div>
                    )}
                    {hasPillars && (
                      <div className='information-item'>
                        <div className='topic'>KEY CAPABILITIES / PILLARS</div>
                        <ul className="capabilities-list">
                          {detail[ContentDetailTableKey.Pillar]
                            .filter(item => !isEmptyOrDash(item.pillarTh) || !isEmptyOrDash(item.pillarEn))
                            .map((item, idx) => (
                              <li key={idx}>{language === 'TH' ? item.pillarTh : item.pillarEn}</li>
                            ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              )}
              {hasUseCases && (
                <div className="information" id='usecase-table'>
                  <h2 className="information-title">Use Cases / Initiatives</h2>
                  <div className='information-content'>
                    <div className='information-item' id='usecase-table'>
                      <ConfigProvider
                        theme={{
                          components: {
                            Table: {
                              colorBgContainer: 'linear-gradient(90deg, #DEC3AA 0%, #DFC5AE 100%)',
                              headerBg: 'transparent',
                              rowHoverBg: 'rgba(255, 255, 255, 0)',
                              borderColor: 'rgba(147, 147, 147, 1)',
                              fontFamily: 'SCG Regular',
                              headerColor: '#000',
                              headerSplitColor: 'rgba(0, 0, 0, 0)',
                              fontSize: 18,
                              cellFontSize: 16,
                              borderRadius: 50
                            },
                          },
                        }}
                      >
                        <div className='box-container'>
                          {detail[ContentDetailTableKey.UseCases]
                            .filter(useCase => {
                              const hasName = !isEmptyOrDash(useCase.nameTh) || !isEmptyOrDash(useCase.nameEn);
                              const hasDetails = Array.isArray(useCase.details) ?
                                useCase.details.some((detail: any) =>
                                  !isEmptyOrDash(detail.currentStepTh) || !isEmptyOrDash(detail.currentStepEn) ||
                                  !isEmptyOrDash(detail.nextMoveTh) || !isEmptyOrDash(detail.nextMoveEn) ||
                                  !isEmptyOrDash(detail.potentialValueTh) || !isEmptyOrDash(detail.potentialValueEn)
                                ) : false;
                              return hasName || hasDetails;
                            })
                            .map((useCase, idx) => renderUseCaseTable(useCase, idx))}
                        </div>
                      </ConfigProvider>
                    </div>
                  </div>
                </div>
              )}
              {showTechnologiesAndEnablers && (
                <div className="information">
                  <h2 className="information-title">Technologies & Enablers</h2>
                  <div className='information-content'>
                    <div className='information-item'>
                      <div className='technology-container'>
                        {hasTechnology && (
                          <h2>{language === 'TH' ? detail[ContentDetailTableKey.Technology].titleTh : detail[ContentDetailTableKey.Technology].titleEn}</h2>
                        )}
                        {hasKeyChallenges && (
                          <ul className="key-challenges-list">
                            {detail[ContentDetailTableKey.KeyChallenges]
                              .filter(item => !isEmptyOrDash(item.challengeTh) || !isEmptyOrDash(item.challengeEn))
                              .map((item, idx) => (
                                <li key={idx}>{language === 'TH' ? item.challengeTh : item.challengeEn}</li>
                              ))}
                          </ul>
                        )}
                        {hasTechnologyImages && (
                          <div className='image-container'>
                            {detail[ContentDetailTableKey.TechnologyImage].images
                              .filter(img => !isEmptyOrDash(img))
                              .map(assetSK => (
                                <TechnologyImageDisplay key={assetSK} assetSK={assetSK} />
                              ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {hasTimeline && (
                <div className="information">
                  <h2 className="information-title">Timeline</h2>
                  <div className='information-content'>
                    <div className='information-item'>
                      <div className='timeline-container'>
                        <Timeline
                          style={{ padding: '0px 16px' }}
                          items={renderTimelineItems()}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={language === 'TH' ? "ไม่มีข้อมูล" : "No data available"} />}
        </div>

        {/* Footer */}
        <footer className="detail-footer">
          <div className="footer-content">
            <img src={dxLogo} alt="DX Logo" className="footer-logo" />
            <div className='company-detail'>
              <h3>Operations, Cement & <br />Green Solution Business</h3>
              <p>© 2025 Digital Transformation. All rights reserved.</p>
            </div>
            <Button
              className="scroll-to-top-btn"
              type="text"
              shape="circle"
              icon={<ArrowUpOutlined />}
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              style={{ width: '52px', height: '52px', borderRadius: '50px' }}
            />
          </div>
        </footer>
      </Content>
    </Layout>
  );
};

export default DetailPage;
